import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell } from 'recharts';
import { AlertTriangle, AlertCircle, Check, X as XIcon, TrendingUp, Receipt, Plane, Clock, ArrowRight } from 'lucide-react';
import { fmtUSD } from './utils';
import logger from '../../utils/logger';

const SEVERITY_STYLES = {
  critical: 'bg-red-50 border-red-300 text-[#D9534F]',
  warning: 'bg-[#FFF3E0] border-[#FFB74D] text-[#E65100]',
  info: 'bg-[#E3F2FD] border-[#64B5F6] text-[#1565C0]',
};

function AlertsStrip({ alerts, onNavigate }) {
  if (!alerts.length) {
    return (
      <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#81C784] bg-[#E8F5E9] text-[#1B5E20]" data-testid="alert-strip-ok">
        <Check className="h-4 w-4" />
        <span className="text-sm font-semibold">All clear — no overdue bills, pending approvals, or at-risk budgets.</span>
      </div>
    );
  }
  return (
    <div className="space-y-2" data-testid="alert-strip">
      {alerts.map(a => (
        <button key={a.key} onClick={() => onNavigate?.(a.link)}
          className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg border text-left transition-all hover:-translate-y-px hover:shadow-sm ${SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info}`}
          data-testid={`alert-${a.key}`}>
          <div className="flex items-center gap-3 min-w-0">
            {a.severity === 'critical' ? <AlertCircle className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
            <div className="min-w-0">
              <p className="text-sm font-bold truncate">{a.title}</p>
              <p className="text-[11px] opacity-80 truncate">{a.detail}</p>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 opacity-60 shrink-0" />
        </button>
      ))}
    </div>
  );
}

function RevenueTrendChart({ data }) {
  const chartData = (data?.months || []).map(m => ({
    label: m.label.split(' ')[0],
    Explore: m.day_program,
    Employment: m.employment,
    IHCS: m.in_home,
    'Private Pay': m.private_pay || 0,
    total: m.total,
    isCurrent: m.month === data?.today_month,
  }));
  return (
    <Card className="border-[#DED8EB] bg-white shadow-sm" data-testid="revenue-trend-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#8E85A0]">Revenue Trend</p>
            <p className="text-xs text-[#8E85A0] mt-0.5">Last 12 months · stacked by service</p>
          </div>
          <TrendingUp className="h-4 w-4 text-[#6B3FA0]" />
        </div>
        <div style={{ width: '100%', height: 240 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#8E85A0' }} tickLine={false} axisLine={{ stroke: '#DED8EB' }} />
              <YAxis tick={{ fontSize: 10, fill: '#8E85A0' }} tickLine={false} axisLine={{ stroke: '#DED8EB' }}
                tickFormatter={v => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} width={45} />
              <Tooltip
                cursor={{ fill: '#F0EDF6' }}
                contentStyle={{ border: '1px solid #DED8EB', borderRadius: 8, fontSize: 12 }}
                formatter={(v) => fmtUSD(v)}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={10} />
              <Bar dataKey="Explore" stackId="a" fill="#6B3FA0" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Employment" stackId="a" fill="#E07A5F" radius={[0, 0, 0, 0]} />
              <Bar dataKey="IHCS" stackId="a" fill="#81C784" radius={[0, 0, 0, 0]} />
              <Bar dataKey="Private Pay" stackId="a" fill="#4FC3F7" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

const AGING_COLORS = {
  '0-30': '#81C784',
  '31-60': '#FFB74D',
  '61-90': '#FB8C00',
  '90+': '#D9534F',
};

function AgingBar({ aging }) {
  const total = aging?.total || 0;
  return (
    <Card className="border-[#DED8EB] bg-white shadow-sm" data-testid="ar-aging-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#8E85A0]">Accounts Receivable Aging</p>
            <p className="text-xs text-[#8E85A0] mt-0.5">Outstanding {fmtUSD(total)}</p>
          </div>
          <Receipt className="h-4 w-4 text-[#1565C0]" />
        </div>
        {total === 0 ? (
          <p className="text-sm text-[#8E85A0] py-6 text-center">No outstanding invoices.</p>
        ) : (
          <>
            <div className="flex w-full h-6 rounded-lg overflow-hidden border border-[#DED8EB]">
              {(aging?.buckets || []).map(b => (
                b.amount > 0 && (
                  <div key={b.bucket}
                    style={{ width: `${b.pct}%`, backgroundColor: AGING_COLORS[b.bucket] }}
                    className="flex items-center justify-center text-[10px] font-bold text-white overflow-hidden"
                    title={`${b.bucket}: ${fmtUSD(b.amount)} (${b.pct}%)`}
                    data-testid={`aging-bar-${b.bucket}`}>
                    {b.pct >= 12 && `${b.pct}%`}
                  </div>
                )
              ))}
            </div>
            <div className="grid grid-cols-4 gap-2 mt-3">
              {(aging?.buckets || []).map(b => (
                <div key={b.bucket} className="text-center" data-testid={`aging-cell-${b.bucket}`}>
                  <div className="w-2 h-2 rounded-full mx-auto mb-1" style={{ backgroundColor: AGING_COLORS[b.bucket] }} />
                  <p className="text-[10px] text-[#8E85A0] uppercase tracking-wider font-semibold">{b.bucket}</p>
                  <p className="text-sm font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtUSD(b.amount)}</p>
                  <p className="text-[10px] text-[#8E85A0]">{b.count} {b.count === 1 ? 'invoice' : 'invoices'}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DueSoonList({ upcoming, onPay, onNavigate }) {
  const bills = upcoming?.bills || [];
  return (
    <Card className="border-[#DED8EB] bg-white shadow-sm" data-testid="ap-upcoming-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-[#8E85A0]">Bills Due Soon</p>
            <p className="text-xs text-[#8E85A0] mt-0.5">Next 30 days · {upcoming?.count || 0} upcoming</p>
          </div>
          <Clock className="h-4 w-4 text-[#E65100]" />
        </div>
        {bills.length === 0 ? (
          <p className="text-sm text-[#8E85A0] py-6 text-center">Nothing due in the next 30 days.</p>
        ) : (
          <div className="space-y-1.5" data-testid="due-soon-list">
            {bills.slice(0, 5).map(b => {
              const isOverdue = b.status === 'overdue';
              const isUrgent = b.days_until_due <= 3;
              return (
                <div key={b.id} className="flex items-center justify-between gap-2 p-2 rounded-md border border-[#F0EDF6] hover:bg-[#F7F4EF] transition-colors" data-testid={`due-row-${b.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-[#2D1B4E] truncate">{b.vendor_name}</p>
                    <p className="text-[10px] text-[#8E85A0]">{b.category}{b.reference ? ` · #${b.reference}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge className={`text-[10px] border ${isOverdue ? 'bg-red-50 text-[#D9534F] border-red-300' : isUrgent ? 'bg-[#FFF3E0] text-[#E65100] border-[#FFB74D]' : 'bg-[#F0EDF6] text-[#5A4D6E] border-[#DED8EB]'}`}>
                      {isOverdue ? `${Math.abs(b.days_until_due)}d overdue` : `${b.days_until_due}d`}
                    </Badge>
                    <p className="text-sm font-bold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtUSD(b.amount)}</p>
                    <Button size="icon" variant="ghost" onClick={() => onPay(b)} title="Mark paid" className="h-6 w-6 text-[#1B5E20] hover:bg-[#E8F5E9]" data-testid={`due-pay-${b.id}`}><Check className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              );
            })}
            {bills.length > 5 && (
              <button onClick={() => onNavigate?.('ap')} className="w-full text-[10px] text-[#6B3FA0] font-semibold hover:underline py-1" data-testid="due-view-all">
                + {bills.length - 5} more bills → View all in AP tab
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function FinanceDashboard({ api, dash, onNavigate, onRefresh }) {
  const [trend, setTrend] = useState(null);
  const [aging, setAging] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [alerts, setAlerts] = useState([]);

  const fetchAll = useCallback(async () => {
    try {
      const [t, a, u, al] = await Promise.all([
        api.get('/finance/revenue-trend?months=12'),
        api.get('/finance/ar-aging'),
        api.get('/finance/ap-upcoming?days=30'),
        api.get('/finance/alerts'),
      ]);
      setTrend(t.data); setAging(a.data); setUpcoming(u.data); setAlerts(al.data.alerts || []);
    } catch (e) { logger.error(e); }
  }, [api]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const markPaid = async (bill) => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      await api.put(`/finance/ap/${bill.id}`, { payment_date: today });
      fetchAll();
      onRefresh?.();
    } catch { /* ignore */ }
  };

  if (!dash) return <p className="text-sm text-[#8E85A0]">Loading…</p>;

  const netPosition = (dash.revenue_month_projected || 0) - (dash.expenses_mtd || 0) - (dash.ap_pending_total || 0) + (dash.ar_outstanding_total || 0);

  return (
    <div className="space-y-5" data-testid="finance-dashboard-enhanced">
      <AlertsStrip alerts={alerts} onNavigate={onNavigate} />

      {/* Top stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-[#DED8EB] bg-white shadow-sm" data-testid="stat-revenue-mtd">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8E85A0]">Revenue (Full Month)</p>
              <TrendingUp className="h-4 w-4 text-[#2E7D32]" />
            </div>
            <p className="text-2xl font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtUSD(dash.revenue_month_projected)}</p>
            <p className="text-[10px] text-[#8E85A0] mt-1">{fmtUSD(dash.revenue_mtd)} booked MTD · projected through {dash.month_end}</p>
          </CardContent>
        </Card>

        <Card className="border-[#DED8EB] bg-white shadow-sm" data-testid="stat-ar-outstanding">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8E85A0]">AR Outstanding</p>
              <Receipt className="h-4 w-4 text-[#1565C0]" />
            </div>
            <p className="text-2xl font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtUSD(dash.ar_outstanding_total)}</p>
            <p className="text-[10px] text-[#8E85A0] mt-1">{fmtUSD(dash.ar_paid_mtd)} paid MTD</p>
          </CardContent>
        </Card>

        <Card className="border-[#DED8EB] bg-white shadow-sm" data-testid="stat-ap-pending">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8E85A0]">AP Due</p>
              <Receipt className="h-4 w-4 text-[#E65100]" />
            </div>
            <p className="text-2xl font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{fmtUSD(dash.ap_pending_total)}</p>
            <p className="text-[10px] text-[#8E85A0] mt-1">{fmtUSD(dash.ap_overdue_total)} overdue · {fmtUSD(dash.ap_due_next_7)} in 7 days</p>
          </CardContent>
        </Card>

        <Card className="border-[#DED8EB] bg-white shadow-sm" data-testid="stat-reimb-pending">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8E85A0]">Reimbursements</p>
              <Plane className="h-4 w-4 text-[#6B3FA0]" />
            </div>
            <p className="text-2xl font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{dash.reimbursements_pending_count} pending</p>
            <p className="text-[10px] text-[#8E85A0] mt-1">{fmtUSD(dash.reimbursements_pending_total)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Revenue trend (wide) + aging (narrow) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          {trend && <RevenueTrendChart data={trend} />}
        </div>
        <AgingBar aging={aging} />
      </div>

      {/* Row 3: Due Soon + Net Position */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2">
          <DueSoonList upcoming={upcoming} onPay={markPaid} onNavigate={onNavigate} />
        </div>
        <Card className="border-[#DED8EB] bg-gradient-to-br from-[#EDE7F6] to-white shadow-sm" data-testid="net-position-card">
          <CardContent className="p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-[#8E85A0]">Projected Net Position</p>
            <p className="text-3xl font-extrabold mt-1" style={{ fontFamily: 'Manrope, sans-serif', color: netPosition >= 0 ? '#2E7D32' : '#D9534F' }}>
              {fmtUSD(netPosition)}
            </p>
            <p className="text-[10px] text-[#8E85A0] mt-1">End-of-month revenue + AR − expenses − AP pending</p>
            <div className="mt-3 pt-3 border-t border-[#DED8EB] space-y-1 text-[11px] text-[#5A4D6E]">
              <div className="flex justify-between"><span>Projected revenue</span><span className="font-mono">{fmtUSD(dash.revenue_month_projected)}</span></div>
              <div className="flex justify-between"><span>+ AR outstanding</span><span className="font-mono">{fmtUSD(dash.ar_outstanding_total)}</span></div>
              <div className="flex justify-between text-[#D9534F]"><span>− Expenses MTD</span><span className="font-mono">{fmtUSD(dash.expenses_mtd)}</span></div>
              <div className="flex justify-between text-[#D9534F]"><span>− AP pending</span><span className="font-mono">{fmtUSD(dash.ap_pending_total)}</span></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
